import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { withFragments } from '@sorare/core/src/lib/gql';

import U23EligibleIcon from '@sorare/marketplace/src/components/card/FootballCardProperties/U23EligibleIcon';

import { RuleHelperFnReturnType } from '../types';
import { getAgeRule_so5Rules } from './__generated__/getAgeRule.graphql';

const Wrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  width: var(--double-unit);
  height: var(--double-unit);
  svg {
    position: absolute;
    inset: 0;
  }
  span {
    position: relative;
    font-size: 9px;
    font-weight: var(--t-bold);
  }
`;

const DefaultAgeIcon = ({ children }: { children?: ReactNode }) => (
  <Wrapper>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 16L16 13.4737V0L8 2.52632L0 0V13.4737L8 16Z"
        fill="url(#paint0_linear_371_2304)"
      />
      <path
        d="M7.84943 3.00311L8 3.05065L8.15057 3.00311L15.5 0.682233V13.1072L8 15.4757L0.5 13.1072V0.682233L7.84943 3.00311Z"
        stroke="white"
        strokeOpacity="0.07"
      />
      <defs>
        <linearGradient
          id="paint0_linear_371_2304"
          x1="13.2485"
          y1="14.0377"
          x2="2.75153"
          y2="1.96232"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0272" stopColor="#391885" />
          <stop offset="0.961" stopColor="#C43BA9" />
        </linearGradient>
      </defs>
    </svg>
    {children && <span>{children}</span>}
  </Wrapper>
);

export const getAgeRule = withFragments(
  (rules: getAgeRule_so5Rules, error: string): RuleHelperFnReturnType => {
    const rule = rules.age;
    if (!rule) {
      return null;
    }

    const isU23 = !rule?.min && rule?.max === 23;

    const minAndMax = [rule?.min, rule?.max].filter(Boolean);

    return {
      id: 'age',
      icon: (
        <>
          {isU23 ? (
            <U23EligibleIcon size={16} />
          ) : (
            <DefaultAgeIcon>
              {minAndMax.length === 1 && minAndMax[0]}
            </DefaultAgeIcon>
          )}
        </>
      ),
      label: (
        <FormattedMessage
          id="Rules.age.label"
          defaultMessage="{min, select, null {{max, select, null {} other {Maximum age of {max}} }} other {Minimum age of {min}{max, select, null {} other{, maximum: {max}}}}}"
          values={{
            min: rule?.min,
            max: rule?.max,
          }}
        />
      ),
      description: (
        <FormattedMessage
          id="Rules.age.description"
          defaultMessage="As of {cutOffDate, date, long}"
          values={{
            cutOffDate: rule.cutOffDate,
          }}
        />
      ),
      error,
    };
  },
  {
    so5Rules: gql`
      fragment getAgeRule_so5Rules on So5Rules {
        id
        age {
          min
          max
          cutOffDate
        }
      }
    ` as TypedDocumentNode<getAgeRule_so5Rules>,
  }
);
